import { useActiveTheme } from '@moko/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  FluentProvider,
  Input,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  OverlayDrawer,
  Spinner,
  TagGroup,
  Textarea,
  tokens,
  type TagGroupProps,
} from '@fluentui/react-components';
import { Dismiss24Regular, People24Regular } from '@fluentui/react-icons';
import {
  PagedDataGrid,
  useIsMobile,
  type IPagedDataGridRefType,
  type DataGridColumn,
} from '@moko/ui';
import {
  getContractors,
  type IContractor,
} from '../../services/contractorService';

type IContractorItem = IContractor & { [key: string]: unknown };

export interface IWorkOfferSubmission {
  contractorIds: number[];
  message?: string;
  offeredPrice?: number;
}

interface WorkOfferPanelProps {
  isOpen: boolean;
  sending?: boolean;
  onDismiss: () => void;
  onSendOffers: (submission: IWorkOfferSubmission) => void;
}

interface WorkOfferFormValues {
  message: string;
  price: string;
}

const contractorTagLabel = (contractor: IContractorItem): string =>
  [contractor.code, contractor.name].filter(Boolean).join(' | ');

const WorkOfferPanel = ({
  isOpen,
  sending = false,
  onDismiss,
  onSendOffers,
}: WorkOfferPanelProps) => {
  const { t } = useTranslation();
  const theme = useActiveTheme();
  const isMobile = useIsMobile();
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const [selected, setSelected] = useState<IContractorItem[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<WorkOfferFormValues>({
    mode: 'onChange',
    defaultValues: {
      message: '',
      price: '',
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setSelected([]);
      reset();
    }
  }, [isOpen, reset]);

  const columns: DataGridColumn<IContractorItem>[] = [
    { key: 'id', name: t('id', 'Id'), width: 50 },
    { key: 'code', name: t('code', 'Code'), width: 125 },
    { key: 'name', name: t('name', 'Name'), width: 250 },
  ];

  const onSubmit = handleSubmit((values) => {
    if (selected.length === 0) return;
    onSendOffers({
      contractorIds: selected.map((c) => c.id),
      message: values.message.trim() === '' ? undefined : values.message.trim(),
      offeredPrice: Number(values.price.trim()),
    });
  });

  const dismissContractor: TagGroupProps['onDismiss'] = (_event, data) => {
    const id = Number(data.value);
    if (!Number.isFinite(id)) return;
    gridRef.current?.deselectItem(id);
  };

  return (
    <FluentProvider theme={theme}>
      <OverlayDrawer
        open={isOpen}
        onOpenChange={(_, data) => {
          if (!data.open) onDismiss();
        }}
        position="end"
        size={isMobile ? 'full' : 'medium'}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t('close', 'Close')}
                icon={<Dismiss24Regular />}
                onClick={onDismiss}
              />
            }
          >
            {t('offerWorkTitle', 'Offer work to contractors')}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <form
            onSubmit={onSubmit}
            noValidate
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              width: '100%',
              minHeight: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.spacingVerticalM,
                width: '100%',
                minWidth: 0,
              }}
            >
              <Controller
                control={control}
                name="message"
                render={({ field }) => (
                  <Field label={t('message', 'Message')}>
                    <Textarea
                      value={field.value}
                      resize="vertical"
                      onChange={(_e, data) => field.onChange(data.value)}
                      onBlur={field.onBlur}
                    />
                  </Field>
                )}
              />
              <Controller
                control={control}
                name="price"
                rules={{
                  validate: (v) => {
                    const value = v.trim();
                    if (value === '')
                      return t('priceRequired', 'Price is required.');
                    const parsed = Number(value);
                    if (Number.isNaN(parsed))
                      return t('priceInvalid', 'Enter a valid number.');
                    if (parsed < 0)
                      return t('priceNegative', 'Price cannot be negative.');
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Field
                    label={t('price', 'Price')}
                    required
                    validationState={errors.price ? 'error' : 'none'}
                    validationMessage={errors.price?.message}
                  >
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      value={field.value}
                      onChange={(_e, data) => field.onChange(data.value)}
                      onBlur={field.onBlur}
                    />
                  </Field>
                )}
              />
            </div>
            <Field
              label={t('contractors', 'Contractors')}
              style={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: tokens.spacingVerticalM,
              }}
            >
              {selected.length > 0 && (
                <TagGroup
                  onDismiss={dismissContractor}
                  aria-label={t('selectedContractors', 'Selected contractors')}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: tokens.spacingHorizontalXS,
                    marginBottom: tokens.spacingVerticalS,
                  }}
                >
                  {selected.map((contractor) => (
                    <InteractionTag
                      key={contractor.id}
                      value={String(contractor.id)}
                      appearance="brand"
                      size="small"
                    >
                      <InteractionTagPrimary hasSecondaryAction>
                        {contractorTagLabel(contractor)}
                      </InteractionTagPrimary>
                      <InteractionTagSecondary
                        aria-label={t('remove', 'Remove')}
                      />
                    </InteractionTag>
                  ))}
                </TagGroup>
              )}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  overflow: 'auto',
                }}
              >
                <PagedDataGrid<IContractorItem>
                  ref={gridRef}
                  columns={columns}
                  selectionMode="multiselect"
                  onMultiSelectionChange={(items) => setSelected(items)}
                  getAction={async (
                    abortController,
                    pageSize,
                    pageNo,
                    orderBy,
                    search
                  ) => {
                    const data = await getContractors(
                      abortController,
                      pageSize,
                      pageNo,
                      orderBy,
                      search
                    );
                    return {
                      ...data,
                      items: data.items as IContractorItem[],
                    };
                  }}
                  pageSize={10}
                  loadingConfig={{
                    message: t('loadingContractors', 'Loading contractors...'),
                    icon: (
                      <People24Regular
                        style={{ width: 125, height: 125, color: 'lightgray' }}
                      />
                    ),
                  }}
                />
              </div>
            </Field>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: `${tokens.spacingVerticalM} 0`,
                marginTop: 'auto',
                borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
              }}
            >
              <Button
                type="submit"
                appearance="primary"
                disabled={selected.length === 0 || isSubmitting || sending}
                icon={
                  isSubmitting || sending ? <Spinner size="tiny" /> : undefined
                }
              >
                {t('sendOffers', 'Send Offers')}
              </Button>
            </div>
          </form>
        </DrawerBody>
      </OverlayDrawer>
    </FluentProvider>
  );
};

export default WorkOfferPanel;
